import { useEffect, useState } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import {
  ArrowIcon,
  InfoIcon
} from "../../../front-end-global-components/assets/assets";
import Button from "../../../front-end-global-components/components/Button/Button";
import CheckBox from "../../../front-end-global-components/components/CheckBox/CheckBox";
import ConditionalRender from "../../../front-end-global-components/components/ConditionalRender/ConditionalRender";
import Modal from "../../../front-end-global-components/components/Modal/Modal";
import {
  AbdmConsentHealthInfoTypes,
  consentStatus,
  lockerSetup
} from "../../../utils/constants";
import { isValidArray, isValidObject } from "../../../utils/validators";
import {
  AbdmRequestDenyReasonInputSection,
  ConsentFooter,
  ConsentHeader,
  DisableAutoApproveSection,
  EnableAutoApproveSection,
  ProviderDetails,
  RequestSuspense,
  RevokeAccessSection
} from "./constants";
import { requestsValidation } from "../../../utils/requestsValidation";
import { dateAndTimeConverter } from "../../../front-end-global-components/utils/constants";
import { getPatientsUpdatedRequests } from "../../../redux/patients/actions";

export const SubscriptionRequest = (props) => {
  const [patientsRequestData, setPatientsRequestData] = useState({});
  const [selectAllProviders, setSelectAllProviders] = useState(true);
  const [selectedProvidersData, setSelectedProvidersData] = useState({});
  const [autoApprove, setAutoApprove] = useState(false);
  const [autoApprovePoliciesData, setAutoApprovePoliciesData] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState({});
  const [checkList, setCheckList] = useState({ allProviders: true });
  const [providers, setProviders] = useState([]);
  const [editRequest, setEditRequest] = useState(true);
  const navigate = useNavigate();

  // store careContexts data in state
  useEffect(() => {
    const { currentProfile, careContexts } = props.patients;

    if (currentProfile && careContexts) {
      setProviders(Object.values(careContexts?.[currentProfile]).flat());
    } else {
      setProviders([]);
    }
    // eslint-disable-next-line
  }, [props.patients.careContexts, props.patients.currentProfile]);

  useEffect(() => {
    getPatientsUpdatedRequests({
      type: "subscription",
      id: props.abdmRequestId
    });
  }, [props.abdmRequestId]);

  useEffect(() => {
    if (patientsRequestData?.includedSources?.[0]?.hip?.id) {
      setSelectAllProviders(false);
    }
  }, [patientsRequestData]);

  // find request data and store in state
  useEffect(() => {
    const { currentProfile, requests, autoApprovalPolicies } = props.patients;

    if (
      !(
        props.abdmRequestId &&
        currentProfile &&
        requests &&
        isValidObject(requests?.[currentProfile]) &&
        isValidArray(requests?.[currentProfile]?.[`${props.abdmRequestType}s`])
      )
    ) {
      return;
    }

    const validatedRequests = requestsValidation(
      requests?.[currentProfile],
      autoApprovalPolicies
    )?.[`${props.abdmRequestType}s`];

    const foundRequest = validatedRequests.find(
      (request) => request.requestId === props.abdmRequestId
    );

    setPatientsRequestData(foundRequest);
    // eslint-disable-next-line
  }, [
    props.abdmRequestId,
    props.abdmRequestType,
    props.patients.requests,
    props.patients.currentProfile,
    props.patients.autoApprovalPolicies
  ]);

  // store selected provider data in store
  useEffect(() => {
    const period =
      patientsRequestData?.period ||
      patientsRequestData?.includedSources?.[0]?.period;
    let defaultData = {
      allProviders: {
        from: period?.from,
        to: period?.to,
        requestToAccess:
          patientsRequestData?.includedSources?.[0]?.hiTypes || []
      }
    };

    if (providers) {
      providers.map(
        (provider, _index) =>
          (defaultData = {
            ...defaultData,
            [provider.hip.id]: {
              from: new Date(Date.now() + 5.5 * 60 * 60 * 1000).toISOString(),
              to: new Date(
                new Date().getFullYear(),
                new Date().getMonth() + 6,
                new Date().getDate()
              ).toISOString(),
              requestToAccess: AbdmConsentHealthInfoTypes
            }
          })
      );
    }
    setSelectedProvidersData(defaultData);

    // eslint-disable-next-line
  }, [providers, patientsRequestData]);

  function approveOnClick() {
    let approveData = {};
    if (checkList) {
      Object.keys(checkList).map((checkedId, _index) =>
        Object.keys(selectedProvidersData).forEach((selectedId) => {
          if (selectedId === checkedId) {
            approveData = {
              ...approveData,
              [checkedId]: selectedProvidersData[checkedId]
            };
          }
        })
      );

      if (editRequest) {
        props.editRequest(approveData, patientsRequestData.pin);
      } else {
        props.acceptRequest(approveData, autoApprove, patientsRequestData.pin);
      }
    }
  }

  useEffect(() => {
    const { autoApprovalPolicies } = props.patients;
    if (!isValidObject(autoApprovalPolicies)) {
      return;
    }
    setAutoApprovePoliciesData(autoApprovalPolicies);

    // eslint-disable-next-line
  }, [props.patients.autoApprovalPolicies]);

  function changeCheckList(checked, providerId) {
    if (checked) {
      setCheckList((prev) => ({
        ...prev,
        [providerId]: providerId
      }));
    } else {
      setCheckList((prev) => {
        const updated = { ...prev };
        delete updated[providerId];
        return updated;
      });
    }
  }

  const ProvidersList = (props) => {
    const providersData = [
      ...new Map(
        providers
          .flat()
          .filter((data) => data.hip.type !== "HEALTH_LOCKER")
          .map((data) => ({
            id: data.hip.id,
            name: data.hip.name,
            type: data.hip.type
          }))
          .map((item) => [item.id, item])
      ).values()
    ];

    const showProviders = props.limit
      ? providersData.slice(0, props.limit)
      : providersData;

    return (
      <>
        {isValidArray(showProviders) &&
          showProviders.map((data, index) => (
            <div
              key={index}
              className="display-flex flex-justify-content-space-between flex-align-items-center padding-bottom-large"
              data-cy="providersList"
            >
              <div className="display-flex">
                {(patientsRequestData?.status === consentStatus.pending ||
                  editRequest) && (
                  <CheckBox
                    name={data.name}
                    id={data.name}
                    checked={
                      patientsRequestData?.includedSources
                        ?.map((d) => d.hip.id)
                        .includes(data.id) ||
                      Object.keys(checkList).some((id) => id === data.id) ||
                      selectAllProviders
                    }
                    onChange={(event) => {
                      changeCheckList(event.target.checked, data.id);
                    }}
                  />
                )}
              </div>
              <div
                className="inherit-parent-width flex-justify-content-space-between flex-align-items-center padding-left-default"
                onClick={() => {
                  if (
                    !selectAllProviders ||
                    (patientsRequestData?.status === consentStatus.approved &&
                      !editRequest)
                  ) {
                    setSelectedProvider({
                      id: data?.id,
                      name: data?.name
                    });
                    navigate("providersModal");
                  }
                }}
              >
                <div className="font-color-secondary font-family-gilroy-regular font-size-medium">
                  {data?.name}
                </div>

                {patientsRequestData?.status === consentStatus.pending ||
                editRequest ? (
                  <ArrowIcon />
                ) : (
                  <InfoIcon />
                )}
              </div>
            </div>
          ))}
      </>
    );
  };

  const providersData = [
    ...new Map(
      providers
        .flat()
        .filter((data) => data.hip.type !== "HEALTH_LOCKER")
        .map((data) => ({
          id: data.hip.id,
          name: data.hip.name,
          type: data.hip.type
        }))
        .map((item) => [item.id, item])
    ).values()
  ];

  return (
    <>
      <ConditionalRender
        condition={
          !isValidObject(patientsRequestData) ||
          !Object.values(selectedProvidersData).length > 0
        }
      >
        <RequestSuspense />
      </ConditionalRender>

      <ConditionalRender
        condition={
          isValidObject(patientsRequestData) &&
          Object.values(selectedProvidersData).length > 0
        }
      >
        <ConditionalRender condition={!props.moreProvider}>
          <section className="overflow-y-auto padding-larger">
            <ConsentHeader
              status={patientsRequestData?.status}
              consentRequesterName={patientsRequestData?.hiu?.name}
              consentRequesterSubName={patientsRequestData?.hiu?.id}
            />
            <section className="">
              <div className="font-color-secondary font-family-gilroy-regular font-size-medium padding-bottom-large">
                Requesting access to automatically fetch & store your Health
                records
              </div>
              <div className="padding-left-large padding-right-large">
                {[
                  {
                    title: " Purpose of request",
                    value: patientsRequestData?.purpose?.text
                  },
                  {
                    title: "FROM",
                    value: `${dateAndTimeConverter(
                      patientsRequestData?.includedSources?.[0]?.period.from,
                      "secondDate"
                    )}, ${dateAndTimeConverter(
                      patientsRequestData?.includedSources?.[0]?.period.from,
                      "Time"
                    )}`
                  },
                  {
                    title: "TO",
                    value: `${dateAndTimeConverter(
                      patientsRequestData?.includedSources?.[0]?.period.to,
                      "secondDate"
                    )}, ${dateAndTimeConverter(
                      patientsRequestData?.includedSources?.[0]?.period.to,
                      "Time"
                    )}`
                  },
                  {
                    title: ` ${
                      patientsRequestData?.status === "EXPIRED"
                        ? "EXPIRED ON"
                        : "EXPIRES AT"
                    }`,
                    value: `${dateAndTimeConverter(
                      patientsRequestData?.includedSources?.[0]?.period.to,
                      "secondDate"
                    )}, ${dateAndTimeConverter(
                      patientsRequestData?.includedSources?.[0]?.period.to,
                      "Time"
                    )}`
                  }
                ].map((d) => (
                  <div>
                    <div className="font-color-secondary font-family-gilroy-regular font-size-smaller text-transform-uppercase letter-spacing-4-percentage padding-bottom-small">
                      {d.title}
                    </div>
                    <div className="font-color-secondary font-family-gilroy-regular font-size-medium padding-bottom-large">
                      {d.value}
                    </div>
                  </div>
                ))}

                {patientsRequestData?.status !== consentStatus.expired && (
                  <>
                    {isValidArray(providers) && (
                      <div className="font-color-secondary font-family-gilroy-regular font-size-smaller text-transform-uppercase letter-spacing-4-percentage padding-bottom-small">
                        FROM PROVIDERS
                      </div>
                    )}
                    {(patientsRequestData?.status === consentStatus.pending ||
                      editRequest) && (
                      <div className="display-flex flex-align-items-center margin-bottom-large">
                        <div className="display-flex">
                          <CheckBox
                            name="selectAllProviders"
                            id="selectAllProviders"
                            checked={selectAllProviders}
                            onChange={(event) => {
                              setSelectAllProviders(event.target.checked);
                              if (event.target.checked) {
                                setCheckList({
                                  allProviders: event.target.checked
                                });
                              } else {
                                setCheckList({});
                              }
                            }}
                          />
                        </div>

                        <div
                          className="inherit-parent-width flex-justify-content-space-between flex-align-items-center padding-left-default"
                          onClick={() => {
                            setSelectedProvider({
                              id: "allProviders",
                              name: "All providers"
                            });
                            navigate("providersModal");
                          }}
                        >
                          <div className="font-color-secondary font-family-gilroy-regular font-size-medium">
                            All providers
                          </div>

                          <ArrowIcon />
                        </div>
                      </div>
                    )}

                    <div
                      className={` ${
                        selectAllProviders && "opacity-half"
                      }  overflow-hidden`}
                    >
                      {(patientsRequestData?.status === consentStatus.pending ||
                        editRequest) && <ProvidersList limit={2} />}

                      {patientsRequestData?.status === consentStatus.approved &&
                        !editRequest && <ProvidersList />}
                    </div>

                    {providersData?.length > 2 &&
                      (patientsRequestData?.status === consentStatus.pending ||
                        editRequest) && (
                        <div className="padding-bottom-medium display-flex flex-align-items-center font-family-gilroy-medium font-color-primary">
                          <div
                            onClick={() => {
                              props.moreProviderOnclick(true);
                            }}
                          >
                            {`+${providersData.length - 2} more provider${
                              providersData.length - 2 > 1 ? "s" : ""
                            }`}
                          </div>
                          <ArrowIcon color="#00A000" />
                        </div>
                      )}
                  </>
                )}
              </div>

              {patientsRequestData?.status !== consentStatus.expired &&
                (patientsRequestData?.status === consentStatus.pending ||
                  editRequest) && (
                  <div className="display-flex flex-align-items-start padding-large border-tertiaryColor border-radius-default">
                    <div>
                      <CheckBox
                        name="autoApprove"
                        id="autoApprove"
                        checked={
                          editRequest &&
                          isValidObject(
                            autoApprovePoliciesData &&
                              Object.values(autoApprovePoliciesData).find(
                                (e) =>
                                  e.patient.healthId ===
                                    patientsRequestData.patient.id &&
                                  e.data?.hiu.id ===
                                    patientsRequestData?.hiu?.id &&
                                  e.enabled === true
                              )
                          )
                            ? true
                            : isValidObject(checkList) &&
                              isValidArray(providers)
                            ? autoApprove
                            : false
                        }
                        disabled={
                          isValidObject(checkList) && isValidArray(providers)
                            ? false
                            : true
                        }
                        onChange={(event) => {
                          if (event.target.checked === false) {
                            if (
                              isValidObject(
                                autoApprovePoliciesData &&
                                  Object.values(autoApprovePoliciesData).find(
                                    (e) =>
                                      e.patient.healthId ===
                                        patientsRequestData.patient.id &&
                                      e.enabled === true
                                  )
                              )
                            ) {
                              navigate("disableAutoApproveModal");
                            }
                          } else {
                            navigate("enableAutoApproveModal");
                          }
                          setAutoApprove(event.target.checked);
                        }}
                      />
                    </div>
                    <div className="font-color-secondary font-family-gilroy-regular font-size-medium padding-left-default">
                      I would like to{" "}
                      <span className="font-family-gilroy-medium">
                        auto-approve
                      </span>
                      all future consent requests from{" "}
                      <span className="font-family-gilroy-medium">
                        {patientsRequestData?.hiu?.name}
                      </span>
                    </div>
                  </div>
                )}
            </section>
          </section>

          {patientsRequestData?.hiu?.id !== lockerSetup?.locker && (
            <ConsentFooter
              loading={props.patients.loading}
              approveButtonDisabled={
                isValidObject(checkList) || checkList.allProviders === false
                  ? // &&
                    // isValidArray(providers)
                    false
                  : true
              }
              approveOnClick={() => {
                if (autoApprove && !editRequest) {
                  navigate("verifyPinModal");
                } else {
                  approveOnClick();
                }
              }}
              denyOnClick={() => {
                navigate("denyConsentModal");
              }}
              editOption={editRequest}
              editOnClick={() => {
                setEditRequest(true);
              }}
              editRequest_cancelOnClick={() => {
                setEditRequest(false);
              }}
              revokeAccessOnClick={() => {
                navigate("revokeAccessModal");
              }}
              status={patientsRequestData?.status}
              type={patientsRequestData?.type}
            />
          )}
        </ConditionalRender>

        <ConditionalRender condition={props.moreProvider}>
          <div>
            <div
              className={`${
                selectAllProviders && "opacity-half"
              } padding-top-large padding-bottom-large padding-left-larger padding-right-larger overflow-scroll `}
            >
              <ProvidersList />
            </div>
            <div className="display-flex padding-larger background-color-white position-absolute bottom-0 inherit-parent-width">
              <Button
                text="Save"
                boxShadow={false}
                className="margin-right-small"
                onClick={() => {
                  props.moreProviderOnclick(false);
                }}
              />
              <Button
                text="Back"
                boxShadow={false}
                variant="secondary"
                className="margin-left-small"
                onClick={() => {
                  props.moreProviderOnclick(false);
                }}
              />
            </div>
          </div>
        </ConditionalRender>
      </ConditionalRender>

      <Routes>
        <Route
          path="providersModal"
          element={
            <ShowModal
              onClose={() => {
                navigate(-1);
              }}
              element={
                <ProviderDetails
                  variant="subscription"
                  edit={editRequest}
                  consentRequesterName={selectedProvider.name}
                  status={patientsRequestData?.status}
                  type={patientsRequestData?.type}
                  requestToAccess={
                    selectedProvidersData?.[selectedProvider.id]
                      ?.requestToAccess
                  }
                  from={selectedProvidersData?.[selectedProvider.id]?.from}
                  to={selectedProvidersData?.[selectedProvider.id]?.to}
                  continueOnClick={({ requestToAccess, from, to }) => {
                    setSelectedProvidersData({
                      ...selectedProvidersData,
                      [selectedProvider.id]: {
                        ...selectedProvidersData[selectedProvider.id],
                        requestToAccess,
                        from,
                        to
                      }
                    });
                    navigate(-1);
                  }}
                  backOnClick={() => {
                    navigate(-1);
                  }}
                />
              }
            />
          }
        />

        <Route
          path="denyConsentModal"
          element={
            <ShowModal
              onClose={() => {
                navigate(-1);
              }}
              element={
                <AbdmRequestDenyReasonInputSection
                  loading={props.patients.loading}
                  denyOnClick={() => {
                    if (typeof props.denyRequest === "function") {
                      props.denyRequest(patientsRequestData.requestDenyReason);
                    }
                  }}
                  cancelOnClick={() => {
                    navigate(-1);
                    setPatientsRequestData({
                      ...patientsRequestData,
                      requestDenyReason: ""
                    });
                  }}
                  reasonInputOnChange={(event) => {
                    setPatientsRequestData({
                      ...patientsRequestData,
                      requestDenyReason: event.target.value
                    });
                  }}
                />
              }
            />
          }
        />
        <Route
          path="revokeAccessModal"
          element={
            <RevokeAccessSection
              loading={props.patients.loading}
              revokeOnClick={() => {
                props.revokePermission();
              }}
              cancelOnClick={() => {
                navigate(-1);
              }}
            />
          }
        />

        <Route
          path="disableAutoApproveModal"
          element={
            <ShowModal
              onClose={() => {
                setAutoApprove(true);
                navigate(-1);
              }}
              element={
                <DisableAutoApproveSection
                  loading={props.patients.loading}
                  hospitalName={
                    patientsRequestData?.hiu?.name ||
                    patientsRequestData?.hiu?.id
                  }
                  disableOnclick={() => {
                    if (!isValidObject(autoApprovePoliciesData)) {
                      return;
                    }

                    props.disableAutoApprove({
                      autoApprovalId: Object.values(autoApprovePoliciesData)
                        ?.filter((e) => {
                          return (
                            e.patient.healthId ===
                              patientsRequestData.patient.id &&
                            e.data?.hiu.id === patientsRequestData?.hiu?.id &&
                            e.enabled === true
                          );
                        })
                        .map((e) => e.documentId),
                      requestId: patientsRequestData.requestId
                    });
                  }}
                  cancelOnClick={() => {
                    setAutoApprove(true);
                    navigate(-1);
                  }}
                />
              }
            />
          }
        />

        <Route
          path="enableAutoApproveModal"
          element={
            <ShowModal
              onClose={() => {
                setAutoApprove(false);
                navigate(-1);
              }}
              element={
                <EnableAutoApproveSection
                  loading={props.patients.loading}
                  hospitalName={
                    patientsRequestData?.hiu?.name
                      ? patientsRequestData?.hiu?.name
                      : patientsRequestData?.hiu?.id
                  }
                  enableOnclick={() => {
                    const hasValidApprovalPolicies =
                      isValidObject(autoApprovePoliciesData) &&
                      Object.values(autoApprovePoliciesData).some((e) => {
                        return (
                          e.patient?.healthId ===
                            patientsRequestData?.patient?.id &&
                          e.data?.hiu?.id === patientsRequestData?.hiu?.id
                        );
                      });

                    if (hasValidApprovalPolicies) {
                      props.enableAutoApprove({
                        autoApprovalId: Object.values(
                          autoApprovePoliciesData
                        ).find((e) => {
                          return (
                            e.patient?.healthId ===
                              patientsRequestData?.patient?.id &&
                            e.data?.hiu?.id === patientsRequestData?.hiu?.id
                          );
                        })?.autoApprovalId,
                        requestId: patientsRequestData.requestId
                      });
                    } else {
                      let approveData = {};
                      if (checkList) {
                        Object.keys(checkList).map((checkedId, _index) =>
                          Object.keys(selectedProvidersData).forEach(
                            (selectedId) => {
                              if (selectedId === checkedId) {
                                approveData = {
                                  ...approveData,
                                  [checkedId]: {
                                    ...selectedProvidersData[checkedId],
                                    from: new Date(
                                      Date.now() + 5.5 * 60 * 60 * 1000
                                    ).toISOString()
                                  }
                                };
                              }
                            }
                          )
                        );
                        props.enableAutoApprove({
                          data: approveData,
                          requester: {
                            name: patientsRequestData?.hiu?.name,
                            id: patientsRequestData?.hiu?.id
                          },
                          requestId: patientsRequestData.requestId
                        });
                      }
                    }

                    navigate(-1);
                  }}
                  cancelOnClick={() => {
                    setAutoApprove(false);
                    navigate(-1);
                  }}
                />
              }
            />
          }
        />
      </Routes>
    </>
  );
};

const ShowModal = (props) => {
  return (
    <Modal
      onClose={props.onClose}
      canIgnore={true}
      show={true}
      position="position-fixed bottom-0 left-0 right-0"
      boxShadow="none"
      borderRadius="none"
      width="inherit-parent-width"
      background="background-transparent"
      height="height-fit-to-content"
    >
      <div className="background-white font-family-gilroy-medium user-select-none">
        <section className="max-width-588px margin-horizontal-auto">
          {props.element}
        </section>
      </div>
    </Modal>
  );
};
